/**
 * CompetitiveSection — comparison matrix + moat cards.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Minus, Clock, Shield } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";

function CellIcon({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-[#4FA9A1]" />;
  if (value === false) return <X className="w-4 h-4 text-gray-400" />;
  if (value === "partial") return <Minus className="w-4 h-4 text-yellow-500/70" />;
  if (value === "roadmap" || value === "community")
    return (
      <span className="flex items-center gap-1">
        <Clock className="w-3.5 h-3.5 text-[#60A5FA]/60" />
        <span className="text-[10px] text-gray-400">{value}</span>
      </span>
    );
  return <span className="text-xs text-gray-400">{String(value)}</span>;
}

export default function CompetitiveSection({ data }) {
  const [showMoat, setShowMoat] = useState(false);

  return (
    <PitchSection id="competitive" dark>
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
        {data.title}
      </h2>

      {/* Comparison matrix */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="overflow-x-auto rounded-2xl border border-white/8 mb-10"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0F1826]/80">
              <th className="px-3 py-3 text-left text-gray-400 font-semibold uppercase tracking-wider sticky left-0 bg-[#0F1826]/90 backdrop-blur-sm z-10 min-w-[120px]">
                Platform
              </th>
              {data.features.map((f) => (
                <th key={f.key} className="px-3 py-3 text-center text-gray-400 font-semibold uppercase tracking-wider whitespace-nowrap min-w-[90px]">
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.competitors.map((comp, i) => {
              const isValpha = comp.name === "Valpha.io";
              return (
                <tr
                  key={i}
                  className={`border-t border-white/5 ${
                    isValpha ? "bg-[#3B82F6]/8" : "hover:bg-white/[0.02]"
                  } transition-colors`}
                >
                  <td className={`px-3 py-3 font-medium sticky left-0 z-10 backdrop-blur-sm ${
                    isValpha ? "text-[#60A5FA] bg-[#3B82F6]/8" : "text-gray-300 bg-[#070B14]/90"
                  }`}>
                    {comp.name}
                  </td>
                  {data.features.map((f) => (
                    <td key={f.key} className="px-3 py-3 text-center">
                      <div className="flex justify-center">
                        <CellIcon value={comp[f.key]} />
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </motion.div>

      {/* Moat / Defensibility */}
      <div>
        <button
          onClick={() => setShowMoat(!showMoat)}
          className="flex items-center gap-2 text-white font-semibold mb-4 group"
        >
          <Shield className="w-5 h-5 text-[#A78BFA]" />
          Why We Win — Defensibility & Moat
          <motion.span
            animate={{ rotate: showMoat ? 90 : 0 }}
            className="text-gray-400 text-lg leading-none"
          >
            ›
          </motion.span>
        </button>
        {showMoat && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {data.moat.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#0F1826]/60 border border-[#A78BFA]/15 rounded-xl p-4"
              >
                <h4 className="text-base text-[#C4B5FD] font-semibold mb-1.5">{m.title}</h4>
                <p className="text-sm text-gray-400 leading-relaxed">{m.body}</p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </PitchSection>
  );
}
